import {
  Checkbox,
  CheckboxChangeEvent,
  Col,
  Flex,
  Row,
  Spin,
  message,
} from 'antd';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Entities_FocusMode,
  Entities_Team_Focus_Player,
} from '@suntex.fim/api-client';
import { $GradeLevel, GradeLevel } from '@suntex.fim/enums';
import { useFramework } from '@suntex.fim/framework';
import {
  Button,
  Card,
  CellCheckBox,
  ColumnType,
  HeaderCheckBox,
  Modal,
  SearchInput,
  Select,
  SelectOptions,
  Table,
  Toolbar,
  Tooltip,
} from '@suntex.fim/ui';

import { useTeamInfo } from '../hooks/useTeamInfo';

export interface FocusEditorProps {
  /** Custom renderer for user ID links. If not provided, renders plain text. */
  renderUserLink?: (info: {
    userId: string;
    playerId: number;
    isPrimaryTeam: boolean;
  }) => ReactNode;
}

type GradeLevelKeys = keyof typeof GradeLevel;

// a type having all the levels as keys and values of type boolean
type RecordGradeLevelBoolean = Record<GradeLevelKeys, boolean>;

const MODAL_ROW_GUTTER = 15;

const ALL_GRADE_LEVELS: readonly GradeLevel[] = $GradeLevel.all();

/**
 * returns JSX for focus mode
 */
export function FocusEditor({ renderUserLink }: FocusEditorProps) {
  const { api } = useFramework();
  const team = useTeamInfo();
  const [playerFocusModal, setPlayerFocusModal] = useState(false);
  const [players, setPlayers] = useState<Entities_Team_Focus_Player[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<readonly number[]>(
    [],
  );
  const [selectedTeamFocusValue, setSelectedTeamFocusValue] =
    useState<RecordGradeLevelBoolean>({
      LevelK: false,
      Level1: false,
      Level2: false,
      Level3: false,
      Level4: false,
      Level5: false,
      Level6: false,
      Level7: false,
      Level8: false,
    });

  const [selectedPlayerFocusValue, setSelectedPlayerFocusValue] =
    useState<RecordGradeLevelBoolean>({
      LevelK: false,
      Level1: false,
      Level2: false,
      Level3: false,
      Level4: false,
      Level5: false,
      Level6: false,
      Level7: false,
      Level8: false,
    });

  const [selectedRow, setSelectedRow] = useState<Entities_Team_Focus_Player[]>(
    [],
  );

  const handleCheckboxChange = (event: CheckboxChangeEvent) => {
    const { checked } = event.target;
    const name = event.target.name as keyof RecordGradeLevelBoolean;

    setSelectedTeamFocusValue((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };
  const handlePlayerCheckboxChange = (event: CheckboxChangeEvent) => {
    const { checked } = event.target;
    const name = event.target.name as keyof RecordGradeLevelBoolean;

    setSelectedPlayerFocusValue((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  /**
   * handles Player Focus mode data fetching and sets data in state
   */
  const getPlayerFocusMode = useCallback(
    (playerId: number) => {
      const selectedPlayer = players.find((player) => player.id === playerId);

      // if no explicit levels for the player
      const explicitLevels = selectedPlayer?.focusMode.gradeLevels;
      if (!explicitLevels || explicitLevels.length === 0) {
        // eslint-disable-next-line no-restricted-syntax
        setSelectedPlayerFocusValue({ ...selectedTeamFocusValue });
      } else {
        setSelectedPlayerFocusValue(levelsToRecord(explicitLevels));
      }
    },
    [players, selectedTeamFocusValue],
  );

  const updateSelectedPlayerFocusValue = useCallback(
    (ids: readonly number[]) => {
      if (ids.length > 1) {
        // eslint-disable-next-line no-restricted-syntax
        setSelectedPlayerFocusValue({ ...selectedTeamFocusValue });
      }
      if (ids.length === 1) {
        const firstId = ids[0];
        if (firstId !== undefined) {
          getPlayerFocusMode(firstId);
        }
      }
    },
    [selectedTeamFocusValue, getPlayerFocusMode],
  );

  // Set playerId for sending the message
  const onSelectedPlayerChange = useCallback(
    (value: readonly number[]) => {
      setSelectedPlayerIds(value);
    },
    [setSelectedPlayerIds],
  );

  const updateSelectedFocusValue = useCallback(
    (levels: readonly GradeLevel[]) => {
      const record = levelsToRecord(levels);
      setSelectedTeamFocusValue(record);
      setSelectedPlayerFocusValue(record);
    },
    [setSelectedTeamFocusValue, setSelectedPlayerFocusValue],
  );

  const setTeamFocusMode = async () => {
    if (!team) return;

    try {
      await api.post_Team_Focus({
        teamId: team.id,
        focusMode: { gradeLevels: getFocusModeValue(selectedTeamFocusValue) },
      });

      message.success('Focus mode updated successfully');
    } catch (e) {
      message.error(`Focus mode could not be updated: ${(e as Error).message}`);
    }
  };

  /**
   * handles data fetching and sets data in state
   */
  const getRecords = useCallback(async () => {
    if (!team) return;

    setIsLoading(true);

    try {
      const response = await api.get_Team_Focus({ teamId: team.id });
      const levels = normalizeGradeLevels(response.focusMode.gradeLevels);

      updateSelectedFocusValue(levels);
      setPlayers(
        response.players.map((p) => ({
          ...p,
          focusMode: { gradeLevels: getGradeLevels(p.focusMode) },
        })),
      );
    } finally {
      setIsLoading(false);
    }
  }, [api, team, updateSelectedFocusValue]);

  const columns = useMemo<ColumnType<Entities_Team_Focus_Player>[]>(
    () => [
      {
        id: 'select',
        size: 40,
        enableResizing: false,
        enableSorting: false,
        header: HeaderCheckBox,
        cell: CellCheckBox,
      },
      {
        id: 'userId',
        accessorKey: 'userId',
        header: 'User ID',
        size: 50,
        enableSorting: true,
        cell: (info) => {
          const userId = info.getValue() as string;
          const playerId = info.row.original.id;
          const isPrimaryTeam = info.row.original.isPrimaryTeam;
          const message: string = isPrimaryTeam
            ? 'This team is the primary team for this player.'
            : 'This player is a guest on this team';

          const linkContent = renderUserLink ? (
            renderUserLink({ userId, playerId, isPrimaryTeam })
          ) : (
            <span className="user-id-col">{userId}</span>
          );

          return (
            <Tooltip
              arrow={false}
              classNames={{ root: 'progress-tooltip' }}
              title={message}
            >
              <span>{linkContent}</span>
            </Tooltip>
          );
        },
      },
      {
        header: 'Focus Mode',
        id: 'focusMode',
        accessorFn: (row) => cellCallback(row),
        enableGlobalFilter: true,
      },
      {
        header: 'Name',
        id: 'name',
        accessorKey: 'name',
        enableGlobalFilter: true,
      },
      {
        header: 'Stickers',
        id: 'stickerCount',
        accessorKey: 'stickerCount',
        cell: (info) => (
          <div style={{ textAlign: 'right' }}>
            {info.getValue<number>().toLocaleString()}
          </div>
        ),
      },
    ],
    [renderUserLink],
  );

  const selectedUserOptions: SelectOptions<number> = selectedRow.map(
    (original: Entities_Team_Focus_Player) => ({
      label: original.name || original.userId,
      value: original.id,
    }),
  );

  useEffect(() => {
    onSelectedPlayerChange(selectedUserOptions.map(({ value }) => value || 0));
    // selectedUserOptions is derived from selectedRow, so we only need selectedRow in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow, onSelectedPlayerChange]);

  const setPlayerFocusMode = async (selectedPlayer: readonly number[]) => {
    if (!team) return;

    setIsLoading(true);

    try {
      const levels = getFocusModeValue(selectedPlayerFocusValue);

      await api.post_Player_Focus({
        teamId: team.id,
        playerIds: [...selectedPlayer],
        focusMode: { gradeLevels: levels },
      });

      void getRecords();

      setPlayerFocusModal(false);
      setSelectedRow([]);

      message.success('Player focus mode was updated');
    } catch (e) {
      message.error(
        `Player focus mode could not be updated: ${(e as Error).message}`,
      );
    }

    setIsLoading(false);
  };

  useEffect(() => {
    void getRecords();
  }, [getRecords]);

  return (
    <>
      <Flex vertical gap="30px">
        <h3>Focus Mode{team?.name ? ` | ${team.name}` : ''}</h3>
        <p>
          &ldquo;Focus&rdquo; allows you to curate your students FIM experience,
          limiting them to games at their achievement level. Select the
          appropriate grade level for the whole class, then modify the settings
          for individual students where needed.
        </p>

        {/* Team focus */}
        <Flex
          vertical
          className="graph-tiles"
          justify="space-between"
          gap="10px"
        >
          <h4 className="title">Set Team Focus</h4>

          <Flex className="graph-tiles__checkbox-group" gap="30px" wrap>
            {$GradeLevel.describeAll().map((level) => (
              <GradeLevelCheckbox
                key={level.value}
                level={level.value}
                checkedState={selectedTeamFocusValue}
                onChange={handleCheckboxChange}
                variant="team"
              />
            ))}
          </Flex>

          <Row>
            <Button
              size="small"
              intent="primary"
              disabled={
                !Object.values(selectedTeamFocusValue).some(
                  (value) => value === true,
                )
              }
              onClick={() => void setTeamFocusMode()}
            >
              Save
            </Button>
          </Row>
        </Flex>

        {/* Player focus */}

        <Card>
          <h4 className="title">Set Player Focus</h4>
          <Flex vertical gap=".5em">
            <Toolbar
              right={
                <Flex gap="10px" justify="center" wrap>
                  <Button
                    intent="primary"
                    disabled={!(selectedRow.length > 0)}
                    onClick={() => {
                      updateSelectedPlayerFocusValue(
                        selectedRow.map((p) => p.id),
                      );
                      setPlayerFocusModal(true);
                    }}
                  >
                    Customize Player Focus
                  </Button>
                  {/* Customize player focus modal starts */}
                  <Modal
                    closable={false}
                    centered
                    title="Customize Player Focus"
                    open={playerFocusModal}
                    onOk={() => setPlayerFocusModal(false)}
                    onCancel={() => setPlayerFocusModal(false)}
                    className="player-focus"
                    width="430px"
                    footer={[
                      <>
                        <Button
                          shape="round"
                          intent="secondary"
                          key="back"
                          onClick={() => setPlayerFocusModal(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          shape="round"
                          intent="primary"
                          key="submit"
                          disabled={
                            !Object.values(selectedPlayerFocusValue).some(
                              (value) => value === true,
                            )
                          }
                          onClick={() => {
                            void setPlayerFocusMode(selectedPlayerIds);
                          }}
                        >
                          Update
                        </Button>
                      </>,
                    ]}
                  >
                    {isLoading ? (
                      <Spin />
                    ) : (
                      <>
                        <div className="tag-list-wrapper">
                          <p>Focus for the selected users will be updated.</p>
                          <Flex gap={4} className="section-to" align="center">
                            <Select.Tags
                              allowClear
                              id="to"
                              value={selectedPlayerIds}
                              onChange={onSelectedPlayerChange}
                              options={selectedUserOptions}
                              className="send-msg"
                              popupClassName="send-msg"
                            />
                          </Flex>
                        </div>
                        <Row gutter={[MODAL_ROW_GUTTER, MODAL_ROW_GUTTER]}>
                          {$GradeLevel.describeAll().map((level) => (
                            <GradeLevelCheckbox
                              key={level.value}
                              level={level.value}
                              onChange={handlePlayerCheckboxChange}
                              checkedState={selectedPlayerFocusValue}
                              variant="player"
                            />
                          ))}
                        </Row>
                      </>
                    )}
                  </Modal>
                  {/* Customize player focus modal ends */}
                  <div className="report-search ml-0">
                    <SearchInput
                      value={searchQuery}
                      onChange={setSearchQuery}
                    />
                  </div>
                </Flex>
              }
            />

            <Table<Entities_Team_Focus_Player>
              data={players}
              columns={columns}
              isLoading={isLoading}
              globalFilter={searchQuery}
              initialSorting={[
                {
                  id: 'userId',
                  desc: false,
                },
              ]}
              setSelectedRow={(rows) =>
                setSelectedRow(rows.map((r) => r.original))
              }
            />
          </Flex>
        </Card>
      </Flex>
    </>
  );
}

/* Private components */

function GradeLevelCheckbox(props: {
  level: GradeLevel;
  checkedState: RecordGradeLevelBoolean;
  onChange: (event: CheckboxChangeEvent) => void;
  variant: 'team' | 'player';
}) {
  const levelLabel = $GradeLevel.describe(props.level).label;
  const levelKey = levelLabel.replace(/ /g, '') as GradeLevelKeys;
  const checkbox = (
    <Checkbox
      name={levelKey}
      checked={props.checkedState[levelKey]}
      onChange={props.onChange}
    >
      {levelLabel}
    </Checkbox>
  );

  if (props.variant === 'team') {
    return (
      <Flex className="checkbox-group-item leading-1" gap="8px" align="center">
        {checkbox}
      </Flex>
    );
  }
  return <Col span={12}>{checkbox}</Col>;
}

/* Utility functions */

/** Safely extracts gradeLevels from a focusMode that may be null at runtime despite generated types. */
function getGradeLevels(
  focusMode: Entities_FocusMode | null | undefined,
): GradeLevel[] {
  return focusMode?.gradeLevels ?? [];
}

/**
 * Returns the provided levels, or all levels if undefined/null/empty.
 * An undefined/null gradeLevels means "no restriction" = all levels.
 */
function normalizeGradeLevels(
  levels: GradeLevel[] | null | undefined,
): readonly GradeLevel[] {
  if (!levels || levels.length === 0) {
    return ALL_GRADE_LEVELS;
  }
  return levels;
}

function getFocusModeValue(selectValue: RecordGradeLevelBoolean): GradeLevel[] {
  //get the values that are enabled (true) and map them to GradeLevel[]
  return (Object.keys(selectValue) as GradeLevelKeys[])
    .filter((x) => selectValue[x])
    .map((x) => GradeLevel[x]);
}

function levelsToRecord(
  levels: readonly GradeLevel[],
): RecordGradeLevelBoolean {
  return Object.fromEntries(
    ALL_GRADE_LEVELS.map((lvl) => [
      $GradeLevel.describe(lvl).label.replace(/ /g, ''),
      levels.includes(lvl),
    ]),
  ) as RecordGradeLevelBoolean;
}

function cellCallback(player: Entities_Team_Focus_Player) {
  const levels = player.focusMode.gradeLevels;
  if (levels.length > 0) {
    return levels.map((x) => $GradeLevel.describe(x).label).join(', ');
  } else return 'Same as team';
}
